import React, { useState } from 'react';
import { 
  Phone, 
  ShieldCheck, 
  AlertTriangle, 
  CheckCircle2, 
  Search, 
  Radio, 
  Building2, 
  Smartphone,
  Info,
  PhoneOff
} from 'lucide-react';

export const PhoneCheckerPage: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<any | null>(null);

  const sampleNumbers = [
    { label: 'Optus Impersonation (Robocall)', num: '+61 488 921 404' },
    { label: 'ATO Arrest Threat (VoIP Spoof)', num: '+61 2 6210 9940' },
    { label: 'CommBank Genuine Branch (Sydney)', num: '+61 2 9155 7700' },
    { label: 'Unregistered International VoIP', num: '+61 491 570 159' }
  ];

  const handleAnalyze = (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneNumber.trim()) return;

    setIsAnalyzing(true);
    setTimeout(() => {
      const clean = phoneNumber.replace(/[\s\-\(\)]/g, '');
      const isSus = clean.includes('488') || clean.includes('6210') || clean.includes('491');
      const isBank = clean.includes('9155') || clean.includes('132221');

      if (isBank) {
        setResult({
          number: phoneNumber,
          carrier: 'Telstra Enterprise / CBA Corporate Trunk',
          lineType: 'Australian Fixed-line Enterprise',
          location: 'Sydney, New South Wales, Australia',
          riskScore: 4,
          riskLevel: 'LOW',
          acmaVerified: true,
          spoofingRisk: 'Low (Cryptographically signed STIR/SHAKEN)',
          robocallReports: 0,
          recommendation: 'Number belongs to verified corporate registry. However, remember banks will never ask for SMS NetCodes over incoming calls.'
        });
      } else if (isSus) {
        setResult({
          number: phoneNumber,
          carrier: 'Offshore Cloud SIP / Virtual VoIP Provider',
          lineType: 'Virtual Mobile / Unallocated Range',
          location: 'Non-Geographic / Spoofed Caller ID',
          riskScore: 92,
          riskLevel: 'CRITICAL',
          acmaVerified: false,
          spoofingRisk: 'Extremely High (Unauthenticated Caller ID)',
          robocallReports: 148,
          recommendation: 'DO NOT ANSWER OR RETURN CALL. Reported for automated robodialing impersonating Australian government & telco authorities.'
        });
      } else {
        setResult({
          number: phoneNumber,
          carrier: 'Optus Mobile Network',
          lineType: 'Standard Cellular Mobile',
          location: 'Melbourne, Victoria, Australia',
          riskScore: 32,
          riskLevel: 'MEDIUM',
          acmaVerified: true,
          spoofingRisk: 'Moderate',
          robocallReports: 2,
          recommendation: 'Standard mobile allocation. If the caller demands emergency gift cards or cryptocurrency, hang up immediately.'
        });
      }
      setIsAnalyzing(false);
    }, 600);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-950 text-emerald-300 text-xs font-bold border border-emerald-800">
          <Phone className="w-3.5 h-3.5" />
          <span>AUSTRALIAN TELECOMMUNICATION INTELLIGENCE</span>
        </div>
        <h1 className="text-3xl font-extrabold text-white">Australian Phone Scam & Caller ID Checker</h1>
        <p className="text-xs text-slate-400 max-w-xl mx-auto">
          Verify whether an incoming phone number or SMS sender is a virtual VoIP spoofer, known robodialer, or legitimate Australian organization.
        </p>
      </div>

      {/* Input Box */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 shadow-xl">
        <form onSubmit={handleAnalyze} className="space-y-3">
          <label className="block text-xs font-bold text-slate-300">Enter Australian Mobile or Landline Number:</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="e.g. 0488 921 404 or (02) 9155 7700"
                required
                className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-slate-950 border border-slate-700 text-white font-mono text-xs focus:outline-none focus:border-emerald-500"
              />
            </div>
            <button
              type="submit"
              disabled={isAnalyzing}
              className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-xl font-bold text-xs shadow-lg transition-colors flex items-center justify-center gap-2"
            >
              {isAnalyzing ? 'Analyzing Telco Telemetry...' : 'Inspect Number'}
            </button>
          </div>
        </form>

        <div className="pt-2 border-t border-slate-800 flex flex-wrap items-center gap-2 text-[11px]">
          <span className="text-slate-400">Sample Numbers:</span>
          {sampleNumbers.map((s, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setPhoneNumber(s.num)}
              className="px-2.5 py-1 rounded-lg bg-slate-950 border border-slate-800 hover:border-slate-700 text-slate-300 transition-colors"
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Result Output */}
      {result && (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-2xl animate-fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-4">
            <div>
              <span className="text-[10px] text-slate-400 uppercase font-mono">Telephony Assessment</span>
              <div className="text-xl font-extrabold text-white font-mono">{result.number}</div>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-bold border ${
              result.riskLevel === 'CRITICAL' 
                ? 'bg-rose-950 text-rose-300 border-rose-800' 
                : result.riskLevel === 'MEDIUM' 
                  ? 'bg-amber-950 text-amber-300 border-amber-800' 
                  : 'bg-emerald-950 text-emerald-300 border-emerald-800'
            }`}>
              {result.riskLevel} RISK ({result.riskScore}/100)
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
            <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 space-y-1">
              <span className="text-slate-400">Carrier / Telco Network:</span>
              <div className="font-bold text-white">{result.carrier}</div>
            </div>
            <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 space-y-1">
              <span className="text-slate-400">Line Type:</span>
              <div className="font-bold text-white">{result.lineType}</div>
            </div>
            <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 space-y-1">
              <span className="text-slate-400">Geographic Allocation:</span>
              <div className="font-bold text-white">{result.location}</div>
            </div>
            <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 space-y-1">
              <span className="text-slate-400">Community Robocall Reports:</span>
              <div className="font-bold text-white font-mono">{result.robocallReports} reports</div>
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2 text-xs">
            <h4 className="font-bold text-white flex items-center gap-2">
              <Info className="w-4 h-4 text-emerald-400" />
              <span>Recommended Action</span>
            </h4>
            <p className="text-slate-300 leading-relaxed">{result.recommendation}</p>
          </div>
        </div>
      )}
    </div>
  );
};
